import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getConfigSync } from '../utils/config';
import { clearReturnTo, isSafeReturnTo, storeReturnTo } from '../utils/returnTo';

interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  pictureUrl?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (provider: 'google' | 'microsoft' | 'linkedin') => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get API URL - use relative path in production, absolute for localhost
let API_BASE_URL = getConfigSync().API_URL;

// CRITICAL FIX: Force absolute URL for localhost only to prevent nginx from serving index.html
// In production, use relative path which Nginx will proxy
if (typeof window !== 'undefined') {
  const origin = window.location.origin;
  const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');
  
  if (isLocalhost) {
    // Development: Force absolute URL with correct backend port
    if (!API_BASE_URL || API_BASE_URL.startsWith('/') || API_BASE_URL.includes('localhost:43000')) {
      if (API_BASE_URL && API_BASE_URL.includes('localhost:43000')) {
        API_BASE_URL = API_BASE_URL.replace('localhost:43000', 'localhost:43001');
      } else {
        API_BASE_URL = 'http://localhost:43001/api';
      }
      console.log('[AuthContext] Forced absolute API_BASE_URL for localhost:', API_BASE_URL);
    }
  } else {
    // Production: Ensure we use relative path (don't create absolute URL with frontend port)
    if (API_BASE_URL && API_BASE_URL.startsWith('http') && API_BASE_URL.includes(':43000')) {
      // Extract path from absolute URL
      try {
        const urlObj = new URL(API_BASE_URL);
        API_BASE_URL = urlObj.pathname;
        console.log('[AuthContext] Converted absolute URL to relative path:', API_BASE_URL);
      } catch (e) {
        // Invalid URL, fallback to /api
        API_BASE_URL = '/api';
        console.log('[AuthContext] Using fallback relative path:', API_BASE_URL);
      }
    } else if (!API_BASE_URL || (!API_BASE_URL.startsWith('/') && !API_BASE_URL.startsWith('http'))) {
      // Ensure we have a valid relative path
      API_BASE_URL = '/api';
      console.log('[AuthContext] Using relative path for production:', API_BASE_URL);
    }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      // Use relative path - axios.defaults.baseURL is already set
      const response = await axios.get('/auth/me', {
        withCredentials: true
      });
      setUser(response.data);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const login = (provider: 'google' | 'microsoft' | 'linkedin') => {
    // For login redirect, we need full URL - use API_BASE_URL
    // If it's relative, make it absolute for window.location.href
    let loginUrl = API_BASE_URL.startsWith('/') 
      ? `${window.location.origin}${API_BASE_URL}/auth/${provider}`
      : `${API_BASE_URL}/auth/${provider}`;
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    if (isSafeReturnTo(returnTo)) {
      storeReturnTo(returnTo);
      loginUrl += `${loginUrl.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent(returnTo)}`;
    }
    window.location.href = loginUrl;
  };

  const logout = async () => {
    try {
      // Use relative path - axios.defaults.baseURL is already set
      await axios.post('/auth/logout', {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Leave the page before setUser(null). Clearing auth on /getmyapikey would
      // re-store that claim URL and send the next account back to it.
      clearReturnTo();
      window.location.assign('/login');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
